import { matchesSearch } from '@webbuilder/shared-types'
import { useCallback, useEffect, useId, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { IconCheck, IconChevronDown, IconDownload, IconGift, IconSearch, IconStar, IconTicket } from '@tabler/icons-react'
import type { GrantableOffer } from '../../../lib/api'
import { useI18n } from '../../../lib/i18n'

const ICONS = { membership: IconStar, pack: IconTicket, digital: IconDownload }

/** Selección única y buscable, fuera del modal para no alargarlo ni recortarse. */
export function GrantOfferPicker({ offers, value, disabled, onChange }: {
  offers: GrantableOffer[]
  value: string
  disabled: boolean
  onChange: (id: string) => void
}) {
  const { t } = useI18n()
  const id = useId()
  const trigger = useRef<HTMLButtonElement>(null)
  const menu = useRef<HTMLDivElement>(null)
  const search = useRef<HTMLInputElement>(null)
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [active, setActive] = useState(0)
  const [position, setPosition] = useState<{ top: number; left: number; width: number; maxHeight: number; above: boolean } | null>(null)
  const chosen = offers.find((offer) => offer.offerId === value)
  const shown = offers.filter((offer) => matchesSearch(offer.title, query))
  const SelectedIcon = chosen ? ICONS[chosen.kind] : IconGift

  const close = useCallback(() => { setOpen(false); trigger.current?.focus() }, [])
  const select = (offer: GrantableOffer) => { onChange(offer.offerId); close() }

  useEffect(() => {
    if (!open) return
    const place = () => {
      const rect = trigger.current?.getBoundingClientRect()
      if (!rect) return
      const below = window.innerHeight - rect.bottom - 18
      const above = below < 280 && rect.top > window.innerHeight - rect.bottom
      const width = Math.min(rect.width, window.innerWidth - 24)
      setPosition({ top: above ? rect.top - 6 : rect.bottom + 6, left: Math.max(12, Math.min(rect.left, window.innerWidth - width - 12)), width, maxHeight: Math.min(340, Math.max(100, above ? rect.top - 18 : below)), above })
    }
    const outside = (event: PointerEvent) => {
      if (!menu.current?.contains(event.target as Node) && !trigger.current?.contains(event.target as Node)) setOpen(false)
    }
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); close() }
    }
    place()
    window.addEventListener('resize', place)
    window.addEventListener('scroll', place, true)
    document.addEventListener('pointerdown', outside)
    document.addEventListener('keydown', escape, true)
    return () => {
      window.removeEventListener('resize', place)
      window.removeEventListener('scroll', place, true)
      document.removeEventListener('pointerdown', outside)
      document.removeEventListener('keydown', escape, true)
    }
  }, [open, close])

  const positioned = position !== null
  useEffect(() => { if (open && positioned) search.current?.focus() }, [open, positioned])
  useEffect(() => { menu.current?.querySelector('[data-active="true"]')?.scrollIntoView?.({ block: 'nearest' }) }, [active, query])

  return <>
    <button ref={trigger} type="button" className="dash-grant-offer-trigger" disabled={disabled}
      aria-label={t('contacts.access.what')} aria-haspopup="dialog" aria-expanded={open} aria-controls={open ? id : undefined}
      onClick={() => { setQuery(''); setActive(0); setOpen((current) => !current) }}>
      <span className="dash-collection-thumb" aria-hidden="true">{chosen?.image ? <img src={chosen.image} alt="" /> : <SelectedIcon size={16} />}</span>
      <span className={`dash-collection-offer-copy${chosen ? '' : ' is-placeholder'}`}>
        <strong>{chosen?.title ?? t('contacts.access.chooseOffer')}</strong>
        {chosen ? <small>{t(`contacts.access.kind.${chosen.kind}`)}{chosen.status !== 'active' ? ` · ${t('collections.form.draft')}` : ''}</small> : null}
      </span>
      <IconChevronDown size={16} aria-hidden="true" />
    </button>
    {open && position ? createPortal(
      <div ref={menu} id={id} className={`dash-grant-offer-menu${position.above ? ' is-above' : ''}`} role="dialog" aria-label={t('contacts.access.what')}
        style={{ top: position.top, left: position.left, width: position.width, maxHeight: position.maxHeight }}>
        <label className="dash-grant-offer-search">
          <IconSearch size={16} aria-hidden="true" />
          <input ref={search} role="combobox" aria-label={t('collections.form.search')} aria-expanded="true" aria-controls={`${id}-options`}
            aria-autocomplete="list" aria-activedescendant={shown[active] ? `${id}-${active}` : undefined}
            placeholder={t('collections.form.search')} value={query} onChange={(event) => { setQuery(event.target.value); setActive(0) }}
            onKeyDown={(event) => {
              if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                event.preventDefault(); setActive((index) => Math.max(0, Math.min(shown.length - 1, index + (event.key === 'ArrowDown' ? 1 : -1))))
              } else if (event.key === 'Enter') { event.preventDefault(); if (shown[active]) select(shown[active]) }
            }} />
        </label>
        <div className="dash-grant-offer-options" role="listbox" id={`${id}-options`} aria-label={t('contacts.access.what')}>
          {shown.map((offer, index) => {
            const Icon = ICONS[offer.kind]
            return <button key={offer.offerId} type="button" role="option" id={`${id}-${index}`} aria-selected={offer.offerId === value}
              data-active={index === active} onClick={() => select(offer)}>
              <span className="dash-collection-thumb" aria-hidden="true">{offer.image ? <img src={offer.image} alt="" loading="lazy" /> : <Icon size={16} />}</span>
              <span className="dash-collection-offer-copy"><strong>{offer.title}</strong><small>{t(`contacts.access.kind.${offer.kind}`)}{offer.status !== 'active' ? ` · ${t('collections.form.draft')}` : ''}</small></span>
              {offer.offerId === value ? <IconCheck size={16} aria-hidden="true" /> : null}
            </button>
          })}
          {!shown.length ? <p className="dash-grant-hint">{t('contacts.access.noMatches')}</p> : null}
        </div>
      </div>, document.body,
    ) : null}
  </>
}
